/**
 * @file SetPasswordView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Backdrop, Box, Button, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, Paper, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import "./../Login.css"
import { Navigate } from "react-router-dom";
import { SignButtonTheme, SignLabelTheme, SmalltextTheme, SsoLabelTheme } from "../LoginTheme";
import Snackbar from "@mui/material/Snackbar";
import { AppProgressBar } from "../../utils/AppProgressBar";
import { AppAlert } from "../../utils/AppAlert";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FormControl from "@mui/material/FormControl";
import { SetPasswordViewModel } from "../viewmodels/SetPasswordViewModel";
import { LocationComponent } from "../../utils/LocationComponent";
import { BaseComponent } from "../../home/base/BaseComponent";
import { GetUserDetailResponse } from "../../../services/models/GetUserDetailResponse";
import { ApiError } from "../../../services/models/ApiError";
import { BaseProps } from "../../home/base/BaseProps";

/**
 * A type of Component state
 */
type CompState = {
    errorToggle: boolean, loading: boolean, submitted: boolean, errorMessage: string, isErrorAlert: boolean
    password: string, disableBtn: boolean, showPassword: boolean, authToken: string, name: string, loadingUser?: boolean;
};

/**
 * A class definition for SetPasswordView
 */
export class SetPasswordView extends BaseComponent<BaseProps, CompState> {

    location?: Location;
    viewModel: SetPasswordViewModel

    constructor(props: BaseProps) {
        super(props)
        this.viewModel = new SetPasswordViewModel();
        this.state = {
            errorToggle: false, loading: false, submitted: false, isErrorAlert: true, name: "",
            errorMessage: "", password: "", disableBtn: true, authToken: "", showPassword: false, 
        }; 
    }

    /**
     * Handles show password
     */
    private handleShowPassword = () => {
        this.setState({ showPassword: !this.state.showPassword })
    }

    /**
     * Handles mouse down password
     * @param event React.MouseEvent
     */
    private handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    /**
     * Hides loading
     */
    private hideLoading = () => {
        this.setState({ loading: false })
    }

    /**
     * Shows loading
     */
    private showLoading = () => {
        this.setState({ loading: true })
    }

    /**
     * Hides user loading
     */
    private hideUserLoading = () => {
        this.setState({ loadingUser: false })
    }

    /**
     * Shows user loading
     */
    private showUserLoading = () => {
        this.setState({ loadingUser: true })
    }    

    /**
     * Hides error
     */
    private hideError = () => {
        this.setState({ errorToggle: false })
    }

    /**
     * Toggle Alert type
     * @param errorAlert boolean
     */
    private toggleAlertType = (errorAlert: boolean) => {
        this.setState({ isErrorAlert: errorAlert })
    }

    /**
     * shows errors
     * @param message string
     */    
    private showError = (message: string) => {
        this.setState({ errorToggle: true, errorMessage: message, loading: false })
    }

    /**
     * Sets token
     * @param token string
     */
    private setToken = (token: string) => {
        this.setState({ authToken: token })
    }

    /**
     * Sets username
     * @param username string
     */
    private setUsername = (username: string) => {
        this.setState({ name: username })
    }

    /**
     * Sets submitted
     */
    private setSubmitted = () => {
        this.setState({ submitted: true })
    }

    /**
     * Validates password
     * @param password string
     * @returns 
     */
    private validatePassword = (password: string) => {
        const regexPattern = "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$"
        if (password.match(regexPattern)) {
            return (true)
        }
        return false
    }

    async componentDidMount() {
        await this.created();
        this.subscribeApiEvents();
    }

    async componentWillUnmount() {
        try {
            this.viewModel.onFinishedPassword.unsubscribe();
            this.viewModel.onFinishedPasswordError.unsubscribe();
        } catch(error) {
            this.logger.error("Ignore Error", SetPasswordView.name);
        }
        
    }

    /**
     * Subscribes API events
     */
    private subscribeApiEvents = ()=>{
        this.viewModel.onFinishedPassword.subscribe((response: any)=>{
            this.logger.verbose(`submitCredentials() - ${response}`, SetPasswordView.name);
            this.toggleAlertType(false);
            this.showError("Password set successfully.");
            setTimeout(()=>{
                this.hideLoading();
                this.setSubmitted();
            }, 1000);
        });

        this.viewModel.onFinishedPasswordError.subscribe((error: ApiError) => {
            this.logger.verbose(JSON.stringify(error), SetPasswordView.name);
            this.toggleAlertType(true);
            this.showError(error.message!!); //eslint-disable-line
            this.hideLoading();
        });
    }

    /**
     * Trigger on change of password
     * @param password string
     * @returns 
     */
    private onChangePassword = (password: string) => {
        const disable = password.trim().length < 8;
        this.logger.verbose(`onChangePassword() - ${disable}`, SetPasswordView.name);
        if (!disable && !this.validatePassword(password)) {
            this.setState({ password: password, disableBtn: true });
            this.logger.verbose("Password does not match length", SetPasswordView.name);
            return;
        }
        this.setState({ password: password, disableBtn: disable });
    }

    /**
     * Submits credentials
     * @returns 
     */
    private submitCredentials = async () => {
        if (!this.state.password) {
            const error = "Empty password.";
            this.logger.verbose("Empty password.", SetPasswordView.name);
            this.toggleAlertType(true);
            this.showError(error);
            return;
        }
        this.showLoading();
        this.viewModel.setNewPassword(this.state.authToken, this.state.password);
    }

    /**
     * Handles location updates
     * @param location Location
     */
    private handleLocationUpdate = (location: Location) => {
        this.location = location;
    }

    /**
     * Triggers on component mount
     */
    private created = async () => {
        const query = this.useQuery();
        const token = this.getAuthToken(query);
        if (token && typeof token === "string") {
           this.setToken(token);
        }

        if (!this.isEmpty(token)) {
            this.viewModel.onLoadUser.subscribe((user: GetUserDetailResponse)=>{
                this.hideUserLoading();
                this.setUsername(user.hcp.firstName);
            });
            this.viewModel.onLoadUserError.subscribe( (apiError: ApiError)=>{
                this.hideUserLoading();
                this.toggleAlertType(true);
                this.showError(apiError.message!!); // eslint-disable-line
            });
            this.showUserLoading();
            this.viewModel.getUserDetails(token!!); // eslint-disable-line
        } else {
            this.setSubmitted();
        }
    }

    render() {

        return <React.Fragment>
            <div className="LoginFormContent">
                {<LocationComponent onChange={this.handleLocationUpdate} />}

                {this.state.submitted && <Navigate to="/" replace={true}></Navigate>}
                <Box flexDirection="column"
                    alignItems="center"
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 1,
                            border: "0px" //"1px dashed grey"
                        },

                    }}>
                
                {this.state.loadingUser && <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" style={{ width: "100%", height: "100%" }}
                            id="LoggedInBSCRep">
                            <Grid item xs={3}>
                                <AppProgressBar color="primary" />
                            </Grid>
                            <Grid item xs={3}>
                                <ThemeProvider theme={SsoLabelTheme}>
                                    <Typography className="SSOLabel">Loading...</Typography>
                                </ThemeProvider>
                            </Grid>
                        </Grid>} 
                    {!this.state.loadingUser && <Paper variant="outlined" style={{ width: "45%", maxWidth: "60%"}}> {/* TODO: See why width:75% is not working here */}
                        <ThemeProvider theme={SignLabelTheme}>
                            <Typography className="SignInLabel">Hi {this.state.name}, </Typography>
                        </ThemeProvider>
                        <ThemeProvider theme={SmalltextTheme}>
                            <Typography className="ForgotPasswordSubtitle">Set a password to protect your account.</Typography>
                        </ThemeProvider>
                        <FormControl className="GenericPasswordTextField" variant="outlined" >
                            <InputLabel htmlFor="outlined-adornment-password">Enter your password</InputLabel>
                            <OutlinedInput size="medium"
                                id="outlined-adornment-password"
                                style={{ fontSize: 16, borderRadius: "10px" }}
                                type={this.state.showPassword ? "text" : "password"}
                                onChange={(e) => { this.onChangePassword(e.target.value) }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={this.handleShowPassword}
                                            onMouseDown={this.handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Enter your password"
                            />
                        </FormControl>
                        <Grid container justifyContent="center" alignItems="center" >
                            <Grid item xs={12}>
                                <ThemeProvider theme={SmalltextTheme}>
                                    <Typography className="MinimumPasswordBanner">Minimum password length should be 8 and contain at least one uppercase letter, one number and one special character(e.g. $#@&)</Typography>
                                </ThemeProvider>
                            </Grid>
                            <ThemeProvider theme={SignButtonTheme}>
                                <Button variant="contained" onClick={this.submitCredentials}
                                    className="SaveButton" disabled={this.state.disableBtn} disableElevation>
                                    Save
                                </Button>
                            </ThemeProvider>
                        </Grid>
                    </Paper>}
                </Box>
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={this.state.loading}
                    onClick={this.hideLoading}>
                    <AppProgressBar color="primary" />
                </Backdrop>
            </div>
            <Snackbar open={this.state.errorToggle} autoHideDuration={6000} onClose={this.hideError}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                <AppAlert onClose={this.hideError} severity={this.state.isErrorAlert ? "error" : "success"} sx={{ width: "150%" }}>
                    {`${this.state.errorMessage}`}
                </AppAlert>
            </Snackbar>
        </React.Fragment>
    }

}


export default SetPasswordView;
